import { openLink, openTelegramLink } from '@telegram-apps/sdk-react';
import { useMemo, useState } from 'react';

import { logger } from '@app/logger';

import { useIntersectionObserver } from '@common/hooks';
import { appInstanceService } from '@common/services/appInstanceService';
import { useAppStore } from '@common/stores/useAppStore';
import { useEventEmit } from '@common/utils/events';
import { fetcher } from '@common/utils/fetcher';
import { safeTma } from '@common/utils/safeTma';
import {
  isTelegramLink,
  toOpenableTelegramLink,
} from '@common/utils/telegramLinks';

import { useProfileImpressionsSession } from '@entities/tasks/hooks/useProfileImpressionsSession';
import { TaskCard } from '@entities/tasks/ui/TaskCard';
import * as dailyRewardQuota from '@entities/tasks/utils/dailyRewardQuota';

import { analytics } from '@services/AnalyticsService';
import {
  usePostChannelCheck,
  usePostTaskClick,
  usePostTaskIntegrationSessionStart,
  usePostTaskShownById,
} from '@services/hooks/TasksService';
import { refreshTasksStore } from '@services/StoreRefreshService';
import {
  type Task,
  type TaskIntegrationSessionStatus,
} from '@services/TasksService';

interface TaskListItemProps {
  task: Task;
  taskPosition: number | undefined;
}

export const TaskListItem: React.FC<TaskListItemProps> = ({
  task,
  taskPosition,
}) => {
  const [impressionSent, setImpressionSent] = useState(false);

  const emit = useEventEmit();
  const { setTasks } = useAppStore();
  const { trigger: triggerPostTaskClick } = usePostTaskClick();
  const { trigger: triggerPostChannelCheck } = usePostChannelCheck();
  const { trigger: triggerPostTaskShownById } = usePostTaskShownById();
  const { trigger: triggerPostTaskIntegrationSessionStart } =
    usePostTaskIntegrationSessionStart();

  const [loadingTasks, setLoadingTasks] = useState<Task[]>([]);

  const impressionsSession = useProfileImpressionsSession();

  const isLoading = useMemo(
    () => loadingTasks.some((item) => item.id === task.id),
    [loadingTasks],
  );

  const isAdpanelTask = (task: Task) =>
    task.task_type === 'third_party_adpanel' ||
    task.verification_method === 'adpanel_session';

  const sleep = (ms: number) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

  const getRewardDescription = (
    starReward: number,
    rewardHours: number,
    rewardDays: number,
  ) => {
    const getStarDeclension = (stars: number) => {
      const lastDigit = stars % 10;
      const lastTwoDigits = stars % 100;
      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'звезд';
      if (lastDigit === 1) return 'звезду';
      if (lastDigit >= 2 && lastDigit <= 4) return 'звезды';
      return 'звезд';
    };
    const getDayDeclension = (days: number) => {
      const lastDigit = days % 10;
      const lastTwoDigits = days % 100;
      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'дней';
      if (lastDigit === 1) return 'день';
      if (lastDigit >= 2 && lastDigit <= 4) return 'дня';
      return 'дней';
    };
    const getHourDeclension = (hours: number) => {
      const lastDigit = hours % 10;
      const lastTwoDigits = hours % 100;
      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'часов';
      if (lastDigit === 1) return 'час';
      if (lastDigit >= 2 && lastDigit <= 4) return 'часа';
      return 'часов';
    };

    const parts: string[] = [];
    if (starReward > 0) {
      parts.push(`${starReward} ${getStarDeclension(starReward)}`);
    }
    if (rewardDays > 0) {
      parts.push(`${rewardDays} ${getDayDeclension(rewardDays)} премиума`);
    } else if (rewardHours > 0) {
      parts.push(`${rewardHours} ${getHourDeclension(rewardHours)} премиума`);
    }

    return parts.length > 0
      ? `Начислили ${parts.join(' и ')}`
      : 'Награда начислена';
  };

  const showRewardSuccess = (
    task: Task,
    responseTask?: TaskIntegrationSessionStatus['task'],
  ) => {
    const starReward = responseTask?.star_reward ?? task.star_reward ?? 0;
    const rewardHours = responseTask?.premium_reward_hours ?? 0;
    const rewardDays = responseTask?.premium_days_reward ?? 0;
    const rewardType =
      starReward > 0 && (rewardDays > 0 || rewardHours > 0)
        ? 'mixed'
        : starReward > 0
          ? 'stars'
          : rewardDays > 0
            ? 'premium_days'
            : 'premium_hours';

    analytics.trackTaskRewarded(task.task_type || 'unknown', rewardType);
    emit('success', {
      title: 'Задание выполнено',
      description: getRewardDescription(starReward, rewardHours, rewardDays),
    });
    dailyRewardQuota.markRewarded(task.id);
  };

  const pollAdpanelTask = async (
    task: Task,
    initialStatus: TaskIntegrationSessionStatus,
  ) => {
    if (initialStatus.rewarded) {
      showRewardSuccess(task, initialStatus.task);
      await refreshTasksStore(setTasks);
      return;
    }

    if (!initialStatus.session_id) {
      throw new Error('Missing integration session id');
    }

    let currentStatus = initialStatus;
    const expiresAt = currentStatus.expires_at
      ? new Date(currentStatus.expires_at).getTime()
      : Date.now() + 2 * 60 * 1000;
    const pollingDeadline = Math.min(expiresAt, Date.now() + 2 * 60 * 1000);
    let attempt = 0;

    while (!currentStatus.terminal && Date.now() < pollingDeadline) {
      await sleep(attempt < 8 ? 1500 : 5000);
      attempt += 1;

      currentStatus = await fetcher<TaskIntegrationSessionStatus>({
        url: `/tasks/${task.id}/integration-sessions/${initialStatus.session_id}`,
        method: 'GET',
      });

      if (currentStatus.state === 'converted' && currentStatus.rewarded) {
        showRewardSuccess(task, currentStatus.task);
        await refreshTasksStore(setTasks);
        return;
      }
    }

    if (currentStatus.state === 'converted') {
      emit('warning', {
        title: 'Задание выполнено',
        description: 'Награда пока не начислена. Попробуйте обновить задания',
      });
      await refreshTasksStore(setTasks);
      return;
    }

    emit('warning', {
      title: 'Задание не выполнено',
      description: 'Попробуйте ещё раз или выполните другое',
    });
    await refreshTasksStore(setTasks);
  };

  const handleAdpanelClick = async (task: Task) => {
    analytics.trackTaskClicked(task.task_type || 'unknown');
    setLoadingTasks((prev) => [...prev, task]);
    const actionWindow = window.open('', '_blank');

    try {
      const appInstanceId =
        await appInstanceService.getOrCreateCurrentAppInstanceId();
      const startResponse = await triggerPostTaskIntegrationSessionStart({
        task_id: task.id,
        app_instance_id: appInstanceId,
      });

      if (startResponse.action_url) {
        if (actionWindow) {
          actionWindow.location.href = startResponse.action_url;
        } else {
          openLink(startResponse.action_url);
        }
      } else {
        actionWindow?.close();
      }

      await pollAdpanelTask(task, startResponse);
    } catch (error) {
      actionWindow?.close();
      emit('error', {
        title: 'Задание не загрузилось',
        description: 'Попробуйте позже',
      });
    } finally {
      setLoadingTasks((prev) => prev.filter((t) => t.id !== task.id));
    }
  };

  const handleClick = async (task: Task) => {
    if (task.status === 'completed') {
      return;
    }

    if (loadingTasks.some((item) => item.id === task.id)) {
      return;
    }

    if (isAdpanelTask(task)) {
      await handleAdpanelClick(task);
      return;
    }

    // Track task click
    analytics.trackTaskClicked(task.task_type || 'unknown');

    await triggerPostTaskClick({ task_id: task.id });
    await refreshTasksStore(setTasks);

    // The click is already recorded server-side above; a dead Telegram
    // transport must not reject this async handler and skip the polling setup
    // below, which is what actually completes the task for the user.
    //
    // The fallback matters more here than elsewhere: if the channel never
    // opens, the check below still runs, finds the user has not joined, and
    // tells them «Задание не выполнено» for something they were never given
    // the chance to do.
    const channelLink = task.target_channel_link;

    if (channelLink) {
      const isTgLink = isTelegramLink(channelLink);
      const targetUrl = isTgLink
        ? toOpenableTelegramLink(channelLink)
        : channelLink;

      const opened = isTgLink
        ? safeTma('task.openTelegramLink', () => openTelegramLink(targetUrl))
        : safeTma('task.openLink', () => openLink(targetUrl));

      // The fallback is best-effort and may well be a no-op here, so it
      // reports rather than assuming success. Two reasons it can fail, both
      // specific to this call site:
      //  - transient user activation is a ~5s budget and the two awaits above
      //    have already spent part of it on a connection flaky enough that
      //    the Telegram transport is dead;
      //  - Telegram's Android WebView frequently ships no onCreateWindow
      //    handler, in which case window.open returns null no matter what.
      // Without this we would never learn that the fallback does not work in
      // the wild, and would keep believing these users got their channel.
      if (!opened && !window.open(targetUrl, '_blank', 'noopener,noreferrer')) {
        logger.captureException(
          new Error('Task channel link could not be opened by any route'),
          { tags: { tmaCall: 'task.openLink.fallbackBlocked' } },
        );
      }
    }

    setLoadingTasks((prev) => [...prev, task]);

    setTimeout(async () => {
      try {
        const responsePostChannelCheck = await triggerPostChannelCheck({
          taskId: task.id,
        });

        if (
          responsePostChannelCheck?.status === 'member' &&
          !responsePostChannelCheck.alreadyRewarded
        ) {
          showRewardSuccess(task, responsePostChannelCheck.task);
        }

        if (
          responsePostChannelCheck?.status === 'member' &&
          responsePostChannelCheck.alreadyRewarded
        ) {
          emit('warning', {
            title: 'Задание уже выполнено',
            description: 'Задание уже выполнено и награда начислена',
          });
        }

        if (responsePostChannelCheck?.status === 'left') {
          emit('warning', {
            title: 'Задание не выполнено',
            description: 'Попробуйте ещё раз или выполните другое',
          });
        }
      } catch (error) {
        emit('error', {
          title: 'Задание не найдено',
        });
      }

      setLoadingTasks((prev) => prev.filter((t) => t.id !== task.id));
    }, 3000);
  };

  const onIntersect = async () => {
    if (impressionSent || impressionsSession.hasSeen(task.id)) {
      return;
    }

    setImpressionSent(true);
    impressionsSession.markSeen(task.id);

    // Track task shown
    analytics.trackTaskShown(task.task_type || 'unknown', taskPosition);

    // Persist impression in task_events
    try {
      await triggerPostTaskShownById({
        taskId: task.id,
        position: taskPosition,
      });
    } catch (e) {
      // swallow client-side tracking errors
    }
  };

  const impressionRef = useIntersectionObserver({
    onIntersect,
    threshold: 0.25,
    enabled: !impressionSent,
    minVisibleMs: 250,
    triggerOnce: true,
  });

  return (
    <div ref={impressionRef}>
      <TaskCard
        key={task.id}
        testId={`task-card-${task.id}`}
        title={task.title}
        subtitle={task.description}
        buttonTitle={task.button_title ?? 'Подписаться'}
        premiumHours={
          typeof task.premium_reward_hours === 'number'
            ? task.premium_reward_hours
            : undefined
        }
        premiumDays={task.premium_days_reward}
        onClick={() => handleClick(task)}
        status={task.status}
        loading={isLoading}
      />
    </div>
  );
};
